import classNames from "classnames";
import Quill from "quill";
import { useEffect, useState } from "react";

import "./view.css";

const VIEW_FORMATS = ["bold", "italic", "underline", "link"];

export interface RichTextViewProps {
  value: string;
  className?: string;
}

/**
 * Read-only renderer for HTML authored in the Quill editors. Quill is used only
 * to sanitise: pasting through the same format whitelist the editor uses drops
 * any markup the editor could never have produced (scripts, unknown tags and
 * attributes), and the normalised HTML is then rendered into a plain element so
 * links stay clickable and no editor chrome leaks in. Safe for client-facing
 * surfaces because the rendered HTML is constrained to bold/italic/underline/link.
 */
export function RichTextView({ value, className }: RichTextViewProps) {
  const [html, setHtml] = useState("");

  useEffect(() => {
    const scratch = document.createElement("div");
    scratch.style.display = "none";
    document.body.appendChild(scratch);

    try {
      const quill = new Quill(scratch, {
        formats: VIEW_FORMATS,
        modules: { toolbar: false },
      });
      quill.clipboard.dangerouslyPasteHTML(value ?? "", "silent" as never);

      quill.root.querySelectorAll("a").forEach((anchor) => {
        const href = anchor.getAttribute("href") ?? "";
        if (href && !/^(https?:|mailto:|tel:)/i.test(href)) {
          anchor.setAttribute("href", `https://${href}`);
        }
        anchor.setAttribute("target", "_blank");
        anchor.setAttribute("rel", "noopener noreferrer");
      });

      setHtml(quill.root.innerHTML);
    } finally {
      document.body.removeChild(scratch);
    }
  }, [value]);

  return (
    <div
      className={classNames("rich-text-view", className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
